import { ClientLoggingService } from '../shared/client-logging.service';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { EnvironmentService } from './environment.service';
import { AuthInterceptor } from './auth.interceptor';
import { GrpcWrapperService } from './grpc-wrapper.service';

// Import the generated protobuf types and gRPC clients
import {
  TaskServiceClient,
  ArchitecturalPlanWriteAsyncServiceClient,
} from '../../generated.commonjs/TaskServiceClientPb';
import {
  Task, // Unified task schema proto message
  TaskUpdate, // Task update schema proto message
  GetTaskStatusRequest,
  GetTaskStatusResponse, // Contains Task proto message
  StartAsyncIngestFileRequest,
  StartAsyncIngestFileResponse, // Includes completed_at field
  PageIngestionOptions, // Optional ingestion features (Issue #258)
} from '../../generated.commonjs/task_pb';

// Re-export protobuf types for convenience
export {
  Task,
  TaskUpdate,
  GetTaskStatusRequest,
  GetTaskStatusResponse,
  StartAsyncIngestFileRequest,
  StartAsyncIngestFileResponse,
  PageIngestionOptions,
};

// Re-export the TaskStatus enum from the protobuf
export import TaskStatus = Task.TaskStatus;

/** Identifies one child task within a single aggregate billing authorization. */
export interface BillingOperationContext {
  operationId: string;
  taskCount: number;
  childKey: string;
}

/** Creates a client-generated id that every task request in one batch can share. */
export function createBillingOperationId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `ingestion-${crypto.randomUUID()}`;
  }
  return `ingestion-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Service for managing asynchronous tasks via gRPC.
 * Handles task status checking and file ingestion tasks.
 */
@Injectable({
  providedIn: 'root',
})
export class TaskService {
  private environmentService = inject(EnvironmentService);
  private authInterceptor = inject(AuthInterceptor);
  private grpcWrapper = inject(GrpcWrapperService);

  private static instanceCounter = 0;
  private instanceId: number;
  private client: TaskServiceClient;
  private asyncClient: ArchitecturalPlanWriteAsyncServiceClient;

  constructor() {
    this.instanceId = ++TaskService.instanceCounter;
    ClientLoggingService.log('INFO', `[TaskService ${this.instanceId}] Initialized`);

    // Initialize gRPC clients with authentication
    const apiServer = this.environmentService.getApiServer();
    ClientLoggingService.log('INFO', `[TaskService ${this.instanceId}] Using gRPC API server: ${apiServer}`);

    // Configure gRPC clients with auth interceptor
    const options = {
      unaryInterceptors: [this.authInterceptor],
    };

    this.client = new TaskServiceClient(`https://${apiServer}:443`, null, options);
    this.asyncClient = new ArchitecturalPlanWriteAsyncServiceClient(`https://${apiServer}:443`, null, options);
  }

  /**
   * Gets the current status of a task.
   * Returns an Observable that emits the task status.
   */
  getTaskStatus(taskId: string): Observable<GetTaskStatusResponse> {
    ClientLoggingService.log('INFO', `[TaskService ${this.instanceId}] Getting status for task: ${taskId}`);

    return new Observable<GetTaskStatusResponse>(observer => {
      try {
        // Create the request using protobuf
        const request = new GetTaskStatusRequest();
        request.setTaskId(taskId);

        // Make the gRPC call (authentication handled by interceptor)
        const call = this.client.getTaskStatus(request, null);

        call
          .then((response: GetTaskStatusResponse) => {
            ClientLoggingService.log(
              'INFO',
              `[TaskService ${this.instanceId}] Task status retrieved:`,
              response.toObject()
            );
            observer.next(response);
            observer.complete();
          })
          .catch((error: any) => {
            ClientLoggingService.log('ERROR', `[TaskService ${this.instanceId}] Failed to get task status:`, error);
            observer.error(error);
          });
      } catch (error) {
        ClientLoggingService.log('ERROR', `[TaskService ${this.instanceId}] Error getting task status:`, error);
        observer.error(error);
      }
    });
  }

  /**
   * Starts asynchronous file ingestion for a single page.
   * Uses the ArchitecturalPlanWriteAsyncService for domain-specific operations.
   * Returns an Observable that emits the task response for tracking.
   */
  startAsyncIngestFile(
    projectId: string,
    filename: string,
    pageNumber?: number,
    forceReprocess?: boolean,
    enableOrientationDetection?: boolean,
    generateExplanation?: boolean,
    billingOperation?: BillingOperationContext
  ): Observable<StartAsyncIngestFileResponse> {
    ClientLoggingService.log(
      'INFO',
      `[TaskService ${this.instanceId}] Starting async file ingestion for ${filename} page ${pageNumber} in project ${projectId}`
    );

    return new Observable<StartAsyncIngestFileResponse>(observer => {
      try {
        // Create ingestion options (Issue #258)
        const options = new PageIngestionOptions();
        options.setEnableOrientationDetection(
          enableOrientationDetection !== undefined ? enableOrientationDetection : true
        );
        options.setGenerateExplanation(generateExplanation !== undefined ? generateExplanation : false);

        // Create the request using protobuf
        const request = new StartAsyncIngestFileRequest();
        request.setProjectId(projectId);
        request.setFilename(filename);
        if (pageNumber !== undefined) {
          request.setPageNumber(pageNumber);
        }
        if (forceReprocess !== undefined) {
          request.setForceReprocess(forceReprocess);
        }
        request.setOptions(options);
        if (billingOperation) {
          request.setBillingOperationId(billingOperation.operationId);
          request.setBillingOperationTaskCount(Math.max(1, billingOperation.taskCount));
          request.setBillingOperationChildKey(billingOperation.childKey);
        }

        // Make the gRPC call (authentication handled by interceptor)
        const call = this.asyncClient.startAsyncIngestFile(request, null);

        call
          .then((response: StartAsyncIngestFileResponse) => {
            ClientLoggingService.log(
              'INFO',
              `[TaskService ${this.instanceId}] Async ingestion started:`,
              response.toObject()
            );
            observer.next(response);
            observer.complete();
          })
          .catch((error: any) => {
            ClientLoggingService.log(
              'ERROR',
              `[TaskService ${this.instanceId}] Failed to start async ingestion:`,
              error
            );
            observer.error(error);
          });
      } catch (error) {
        ClientLoggingService.log(
          'ERROR',
          `[TaskService ${this.instanceId}] Error starting async file ingestion:`,
          error
        );
        observer.error(error);
      }
    });
  }

  /**
   * Utility method to wrap gRPC calls with common error handling.
   */
  private wrapGrpcCall<T>(grpcCall: Promise<T>, progressMessage: string): Observable<T> {
    return this.grpcWrapper.wrapWithProgress<T>(grpcCall, progressMessage);
  }

  /**
   * Converts a protobuf Task to a plain object for easier use in templates.
   */
  static taskToObject(task: Task): Task.AsObject {
    return task.toObject();
  }

  /**
   * Converts a protobuf StartAsyncIngestFileResponse to a plain object.
   */
  static startAsyncIngestFileResponseToObject(
    response: StartAsyncIngestFileResponse
  ): StartAsyncIngestFileResponse.AsObject {
    return response.toObject();
  }
}
